import React, { useEffect, useMemo, useState } from 'react';
import { Download, Printer, FileBarChart2 } from 'lucide-react';
import { getCategoriesForPosition, getCategoryScore, getOverallScore } from '../data/criteria';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';
import { PlayersAPI, EvaluationsAPI } from '../api';

export default function Reports() {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [evals, setEvals] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    PlayersAPI.list().then(list => {
      setPlayers(list);
      if (list.length) setPlayerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!playerId) return;
    EvaluationsAPI.list(playerId).then(setEvals);
  }, [playerId]);

  const player = players.find(p => p.id === playerId);
  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);
  const latest = useMemo(() => [...evals].sort((a, b) => new Date(b.date) - new Date(a.date))[0], [evals]);

  const handleExport = () => {
    if (!latest || !player) return;
    const rows = [['Category', 'Criterion', 'Rating']];
    categories.forEach(cat => {
      cat.items.forEach((it, idx) => rows.push([cat.name, it, latest.ratings?.[cat.id]?.[idx] || latest.ratings?.[cat.id]?.[String(idx)] || '']));
    });
    const csv = rows.map(r => r.map(f => `"${String(f).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${player.firstName}_${player.lastName}_evaluation.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast({ title: 'Report exported', description: 'CSV file downloaded.' });
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader title="Reports" subtitle="Generate and export scouting reports." />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-slate-500">Player</Label>
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                {players.map(p => <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  #{p.jerseyNumber}  {p.position}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleExport} disabled={!latest} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">
            <Printer className="h-4 w-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {!latest || !player ? (
        <div className="text-center py-16 text-slate-500">
          <FileBarChart2 className="h-10 w-10 text-slate-700 mx-auto mb-2" />
          No evaluation on record for this player.
        </div>
      ) : (
        <div className="bg-white text-slate-900 rounded-xl overflow-hidden border border-slate-200 shadow-sm print:rounded-none print:shadow-none print:border-0" id="report-doc">
          <div className="bg-slate-900 text-white p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-sky-600 mb-2">ShiftNotes™  Scouting Report</div>
                <h1 className="font-display text-3xl font-bold">{player.firstName} {player.lastName}</h1>
                <div className="text-slate-500 mt-1">#{player.jerseyNumber}  {player.position}  {player.team}</div>
                <div className="text-slate-500 text-xs mt-1">Rubric: {player.position === 'Goalie' ? 'Goalie framework' : 'Skater framework'}</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-5xl bg-gradient-to-br from-sky-600 to-cyan-600 bg-clip-text text-transparent">
                  {getOverallScore(latest.ratings, categories)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall</div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-xs uppercase text-slate-500 tracking-wider">Height / Weight</div><div className="font-semibold">{player.height}  {player.weight}</div></div>
              <div><div className="text-xs uppercase text-slate-500 tracking-wider">Shoots</div><div className="font-semibold">{player.shoots}</div></div>
              <div><div className="text-xs uppercase text-slate-500 tracking-wider">Evaluator</div><div className="font-semibold">{latest.evaluatorName}</div></div>
              <div><div className="text-xs uppercase text-slate-500 tracking-wider">Date</div><div className="font-semibold">{new Date(latest.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div></div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Context</div>
              <div className="text-slate-800">{latest.gameContext}</div>
            </div>

            {latest.notes && (
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Evaluator Notes</div>
                <div className="text-slate-800 italic">{latest.notes}</div>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Category Ratings</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
                {categories.map(c => {
                  const s = parseFloat(getCategoryScore(latest.ratings, c.id, categories));
                  return (
                    <div key={c.id} className="bg-slate-100 rounded p-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.name}</div>
                      <div className="font-mono font-bold text-lg mt-1">{s.toFixed(1)}</div>
                      <div className="h-1 bg-slate-300 rounded-full mt-1.5 overflow-hidden">
                        <div className="h-full bg-sky-500" style={{ width: `${(s / 10) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Detailed Breakdown</div>
              {categories.map(c => (
                <div key={c.id} className="mb-4">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-1 mb-2">
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <Badge className="bg-sky-100 text-sky-800 border-sky-200 font-mono">{getCategoryScore(latest.ratings, c.id, categories)}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    {c.items.map((it, idx) => (
                      <div key={idx} className="flex justify-between border-b border-slate-100 py-1">
                        <span className="text-slate-700 pr-4">{it}</span>
                        <span className="font-mono font-semibold text-slate-900">{latest.ratings?.[c.id]?.[idx] || latest.ratings?.[c.id]?.[String(idx)] || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
